import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { SplitButton } from 'primereact/splitbutton';
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { FaArrowLeft } from 'react-icons/fa';
import { NumericFormat } from 'react-number-format';
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../../Layout/Header";
import { getEndpointUrl, getTimestamp } from "../../Utils/Common";
import AddChoiceGroupDialog from "./AddChoiceGroupDialog";
import UpdateChoiceGroupDialog from "./UpdateChoiceGroupDialog";
import DeleteChoiceGroupDialog from "./DeleteChoiceGroupDialog";

function DetilElement() {
    const { section_id, element_id } = useParams();
    const [isLoading, setLoading] = useState(true);
    const [accesToken] = useState(JSON.parse(localStorage.getItem('credentials')) || []);
    const [elementMeta, setElementMeta] = useState(null);
    const [groups, setGroups] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const inputRef = useRef();
    const [showCreate, setShowCreate] = useState(false);
    const [showUpdate, setShowUpdate] = useState(false);
    const [updateData, setUpdateData] = useState(null);
    const [deleteData, setDeleteData] = useState(null);
    const [showDelete, setShowDelete] = useState(false);

    const navigate = useNavigate();

    const [userLogin, setUserLogin] = useState(
        JSON.parse(localStorage.getItem('knowLogin')) || []
    );

    const fetchElementDetail = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(
                `${getEndpointUrl()}/v2/form_elements/${element_id}?v=${getTimestamp()}`,
                {
                    headers: new Headers({
                        Authorization: `Bearer ${accesToken}`,
                    }),
                }
            );
            const json = await res.json();
            if (json.status === 'ok' && json.results) {
                setElementMeta(json.results);
                setGroups(json.results.form_choice_groups || []);
            } else {
                setElementMeta(null);
                setGroups([]);
            }
        } catch (err) {
            setElementMeta(null);
            setGroups([]);
        } finally {
            setLoading(false);
        }
    }, [element_id, accesToken]);

    useEffect(() => {
        fetchElementDetail();
    }, [fetchElementDetail]);

    const onGlobalFilterChange = (e) => {
        setSearchTerm(e.target.value || '');
    };

    const filteredGroups = (groups || []).filter((grp) => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return true;
        const name = (grp?.name || '').toLowerCase();
        const detailText = (grp?.form_choices_details || [])
            .map((d) => (d?.detail || '').toLowerCase())
            .join(' ');
        return name.includes(term) || detailText.includes(term);
    });

    const booleanTemplate = (val) => (val ? 'Ya' : 'Tidak');

    const detailsCountTemplate = (rowData) => (
        <NumericFormat
            value={(rowData?.form_choices_details || []).length}
            displayType={'text'}
            thousandSeparator={true}
            className="bg-transparent"
            renderText={(value, props) => <span {...props}>{value}</span>}
        />
    );

    const detailsListTemplate = (rowData) => {
        const details = rowData?.form_choices_details || [];
        if (!details.length) return <span className="text-muted">—</span>;
        return (
            <div className="d-flex flex-wrap gap-1">
                {details.map((d) => (
                    <span key={d.id} className="badge bg-secondary">{d.detail}</span>
                ))}
            </div>
        );
    };

    const header = (
        <Row>
            <Row>
                <div className="my-2 d-flex align-items-center gap-2">
                    <Link to={`/form-options/${section_id}`} className="btn btn-outline-secondary my-1">
                        <FaArrowLeft /> Kembali
                    </Link>
                </div>
            </Row>
            <Row>
                <Col md={8} sm={12} className="my-2">
                    <h5 className="fw-bold pt-2">
                        Form Choice Groups — {elementMeta?.name || `Element #${element_id}`}
                    </h5>
                </Col>
                <Col md={4} sm={12} className="my-2 d-flex align-items-center justify-content-end gap-2">
                    <div className="p-input-icon-left" style={{ minWidth: '280px' }}>
                        <i className="pi pi-search" />
                        <InputText
                            ref={inputRef}
                            className="p-inputtext-sm"
                            onChange={onGlobalFilterChange}
                            placeholder="Pencarian nama/detail..."
                            style={{ minWidth: '100%' }}
                        />
                    </div>
                    <div>
                        <Button
                            className="btn btn-secondary"
                            onClick={() => {
                                setShowCreate(true);
                            }}>
                            Tambah
                        </Button>
                    </div>
                </Col>
            </Row>
        </Row>
    );

    const kelolaBodyTemplate = (rowData) => {
        return <SplitButton size="small" label="Lihat detil" icon="pi pi-eye"
            onClick={(e) => {
                navigate(`/form-options/${section_id}/${element_id}/${rowData.id}`);
            }}
            model={[
                {
                    label: 'Edit',
                    icon: 'pi pi-pencil',
                    disabled: userLogin.role != 'admin',
                    command: () => {
                        setUpdateData(rowData);
                        setShowUpdate(true);
                    }
                },
                {
                    label: 'Hapus',
                    icon: 'pi pi-trash',
                    disabled: userLogin.role != 'admin',
                    command: () => {
                        setDeleteData(rowData);
                        setShowDelete(true);
                    }
                }
            ]} severity="secondary" text />
    };

    return (
        <>
            <Helmet>
                <title>Detail Element — {elementMeta?.name || element_id}</title>
            </Helmet>
            <Header />
            <Container className="mt-3">
                <DataTable
                    value={filteredGroups}
                    size="small"
                    responsiveLayout="scroll"
                    dataKey="id"
                    header={header}
                    emptyMessage="Tidak ada data yang ditemukan."
                    paginator
                    rows={15}
                    loading={isLoading}
                >
                    <Column field="id" header="ID" style={{ width: '100px' }} />
                    <Column field="name" header="NAMA GROUP" />
                    <Column header="REQUIRED" body={(rowData) => booleanTemplate(rowData?.required)} style={{ width: '120px' }} />
                    <Column header="DETAIL PILIHAN" body={detailsListTemplate} />
                    <Column header="KELOLA" body={kelolaBodyTemplate} />
                </DataTable>
            </Container>

            <AddChoiceGroupDialog
                visible={showCreate}
                onHide={() => setShowCreate(false)}
                formElementId={parseInt(element_id, 10)}
                onSuccess={(createdGroup) => {
                    setGroups((prev) => [createdGroup, ...prev]);
                }}
            />
            <UpdateChoiceGroupDialog
                visible={showUpdate}
                onHide={() => setShowUpdate(false)}
                group={updateData}
                formElementId={parseInt(element_id, 10)}
                onSuccess={(updatedGroup) => {
                    setGroups((prev) => prev.map((g) => (g.id === updatedGroup.id ? updatedGroup : g)));
                }}
            />
            <DeleteChoiceGroupDialog
                visible={showDelete}
                onHide={() => setShowDelete(false)}
                group={deleteData}
                onSuccess={(deletedId) => {
                    setGroups((prev) => prev.filter((g) => g.id !== deletedId));
                }}
            />
        </>
    );
}

export default DetilElement;